import {ZIDX} from "../standaloneLibrary.js";

interface RAGResultRow {
    title: string;
    url: string;
    displayUrl: string;
    snippetHtml: string; // escaped by the server, only <b> tags
}

/**
 * Google-style search results shown below a chatbot's AI Overview.
 * One panel per chat: every new search query replaces the results and shows page 1.
 * Pagination is ajax; the server caches the ordered list so page changes are instant.
 */
export class RAGResults {
    private zidx: ZIDX;
    private panel: HTMLElement;
    private aiBotId: number;
    private getSessionId: () => string | null;
    private query = "";
    private requestCounter = 0;

    constructor(zidx: ZIDX, panel: HTMLElement, aiBotId: number, getSessionId: () => string | null) {
        this.zidx = zidx;
        this.panel = panel;
        this.aiBotId = aiBotId;
        this.getSessionId = getSessionId;
    }

    /** Show results for a new query, starting at page 1. */
    show(query: string) {
        this.query = query;
        this.loadPage(1, false);
    }

    private async loadPage(page: number, scrollToTop: boolean) {
        const sessionId = this.getSessionId();
        if (!sessionId || !this.query) return;
        const requestId = ++this.requestCounter;
        this.panel.style.display = "block";
        if (this.panel.childElementCount === 0) {
            this.renderMessage("Searching...");
        }
        try {
            const url = this.zidx.options.selectedSiteDomain + "/rag-chat-results/"
                + "?ai_bot_id=" + encodeURIComponent(String(this.aiBotId))
                + "&ai_chat_session_id=" + encodeURIComponent(sessionId)
                + "&q=" + encodeURIComponent(this.query)
                + "&page=" + page;
            const res = await fetch(url, {credentials: "include"});
            const data = await res.json();
            if (requestId !== this.requestCounter) return; // a newer search replaced this one
            if (!data.success) {
                this.renderMessage("Search results are unavailable right now.");
                return;
            }
            this.render(data);
            if (scrollToTop) {
                // stop 50px above the panel, plus the height of a fixed header (embedded chat) so it does not cover the results
                const header = this.panel.closest(".idx-ai-embed")?.querySelector(".idx-ai-header") as HTMLElement | null;
                const headerHeight = header && getComputedStyle(header).position === "sticky" ? header.offsetHeight : 0;
                this.panel.style.scrollMarginTop = (50 + headerHeight) + "px";
                this.panel.scrollIntoView({behavior: "smooth", block: "start"});
            }
        } catch (e) {
            if (requestId !== this.requestCounter) return;
            console.error("Failed to load search results", e);
            this.renderMessage("Search results are unavailable right now.");
        }
    }

    private renderMessage(message: string) {
        this.panel.innerHTML = "";
        const p = document.createElement("p");
        p.className = "idx-ai-results-message";
        p.textContent = message;
        this.panel.appendChild(p);
    }

    private render(data: any) {
        this.panel.innerHTML = "";

        const queryLine = document.createElement("div");
        queryLine.className = "idx-ai-results-query";
        queryLine.appendChild(document.createTextNode("Results for: "));
        const queryText = document.createElement("b");
        queryText.textContent = data.query || this.query;
        queryLine.appendChild(queryText);
        this.panel.appendChild(queryLine);

        const rows: RAGResultRow[] = data.results || [];
        if (rows.length === 0) {
            const none = document.createElement("p");
            none.className = "idx-ai-results-message";
            none.textContent = "No results found.";
            this.panel.appendChild(none);
            return;
        }

        const list = document.createElement("div");
        list.className = "idx-ai-results-list";
        rows.forEach((row, i) => {
            if (i > 0) {
                const rule = document.createElement("hr");
                rule.className = "idx-ai-result-rule";
                list.appendChild(rule);
            }
            list.appendChild(this.renderRow(row));
        });
        this.panel.appendChild(list);

        if (data.pageCount > 1) {
            this.panel.appendChild(this.renderPagination(data.page, data.pageCount));
        }
    }

    private renderRow(row: RAGResultRow): HTMLElement {
        const item = document.createElement("div");
        item.className = "idx-ai-result";

        const displayUrl = document.createElement("div");
        displayUrl.className = "idx-ai-result-url";
        displayUrl.textContent = row.displayUrl || "";
        item.appendChild(displayUrl);

        const title = document.createElement("a");
        title.className = "idx-ai-result-title";
        title.textContent = row.title || row.url;
        if (/^https?:\/\//i.test(row.url)) {
            title.href = row.url;
        }
        title.target = "_blank";
        title.rel = "noopener noreferrer";
        item.appendChild(title);

        const snippet = document.createElement("div");
        snippet.className = "idx-ai-result-snippet";
        snippet.innerHTML = row.snippetHtml || ""; // escaped by the server, only <b> tags
        item.appendChild(snippet);
        return item;
    }

    private renderPagination(page: number, pageCount: number): HTMLElement {
        const nav = document.createElement("nav");
        nav.className = "idx-ai-pagination";
        nav.setAttribute("aria-label", "Search results pages");

        const addLink = (label: string, target: number, extraClass: string, current: boolean) => {
            const a = document.createElement("a");
            a.className = "idx-ai-page-link" + (extraClass ? " " + extraClass : "");
            a.textContent = label;
            a.href = "#";
            if (current) {
                a.classList.add("idx-ai-page-current");
                a.setAttribute("aria-current", "page");
            }
            a.addEventListener("click", (e) => {
                e.preventDefault();
                if (!current) this.loadPage(target, true);
            });
            nav.appendChild(a);
        };

        if (page > 1) addLink("Previous", page - 1, "idx-ai-page-prev", false);
        for (let p = 1; p <= pageCount; p++) {
            addLink(String(p), p, "", p === page);
        }
        if (page < pageCount) addLink("Next", page + 1, "idx-ai-page-next", false);
        return nav;
    }
}
